import Component from '@glimmer/component';
import { service } from '@ember/service';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button, Chip } from 'tio-ui/components/buttons';
import { eq } from 'tio-ui/utilities';
import SectionHeader from 'tio-employee/components/reimagined/section-header';
import SmartActionCard from 'tio-employee/components/reimagined/smart-action-card';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import type { DataSourceInfo } from 'tio-employee/services/reimagined-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import {
  PhFileText,
  PhUploadSimple,
  PhCheckCircle,
  PhWarning,
  PhClock,
  PhShieldCheck,
  PhEye,
  PhCloudArrowUp,
  PhSparkle,
  PhLightbulb,
  PhRobot,
} from 'ember-phosphor-icons';

interface DocumentsIndexModel {
  dataSourceInfo: DataSourceInfo;
}

interface DocumentsIndexSignature {
  Args: {
    model: DocumentsIndexModel;
  };
}

interface Document {
  id: string;
  name: string;
  type: string;
  status: 'verified' | 'pending' | 'action-needed';
  uploadDate: string;
  statusLabel: string;
}

const DEMO_DOCUMENTS: Document[] = [
  {
    id: '1',
    name: 'Student Loan Statement - Jan 2026',
    type: 'Loan Statement',
    status: 'verified',
    uploadDate: 'Jan 15, 2026',
    statusLabel: 'Verified',
  },
  {
    id: '2',
    name: 'Employment Certification Form',
    type: 'ECF',
    status: 'verified',
    uploadDate: 'Dec 10, 2025',
    statusLabel: 'Verified',
  },
  {
    id: '3',
    name: 'W-2 Form 2025',
    type: 'Tax Document',
    status: 'pending',
    uploadDate: 'Feb 1, 2026',
    statusLabel: 'Under Review',
  },
  {
    id: '4',
    name: 'Tuition Invoice - Spring 2026',
    type: 'Tuition Document',
    status: 'action-needed',
    uploadDate: 'Jan 28, 2026',
    statusLabel: 'Action Needed',
  },
];

export default class DocumentsIndex extends Component<DocumentsIndexSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get documents() {
    return DEMO_DOCUMENTS;
  }

  get verifiedCount() {
    return this.documents.filter((d) => d.status === 'verified').length;
  }

  get pendingCount() {
    return this.documents.filter((d) => d.status === 'pending').length;
  }

  get actionNeededCount() {
    return this.documents.filter((d) => d.status === 'action-needed').length;
  }

  get smartActions(): SmartAction[] {
    return [
      {
        id: 'upload',
        title: 'Upload Document',
        description: 'Upload loan statements, tax forms, or other documents for verification.',
        icon: PhUploadSimple,
        priority: 'urgent',
        category: 'deadline',
        cta: 'Upload Now',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'review-pending',
        title: 'Review Pending',
        description: `You have ${this.pendingCount} document(s) awaiting review.`,
        icon: PhClock,
        priority: 'high',
        category: 'deadline',
        cta: 'Review Documents',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'connect-accounts',
        title: 'Connect Accounts',
        description: 'Instantly verify loan data by connecting your servicer accounts.',
        icon: PhShieldCheck,
        priority: 'medium',
        category: 'opportunity',
        cta: 'Connect Now',
        route: 'authenticated.reimagined.advisor',
      },
    ];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="gap-8 pb-12">
      {{! Hero Section }}
      <div
        class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-violet-700 to-purple-800 p-8 text-white"
      >
        <div class="absolute top-0 right-0 -mt-8 -mr-8 h-48 w-48 rounded-full bg-white/5"></div>
        <div class="absolute bottom-0 left-0 -mb-12 -ml-12 h-64 w-64 rounded-full bg-white/5"></div>
        <div class="relative z-10">
          <div class="flex items-center gap-3 mb-4">
            <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
              <PhFileText class="h-7 w-7 text-white" />
            </div>
            <div>
              <Heading @level={{2}} class="text-white text-2xl font-bold">
                Document Center
              </Heading>
              <Text class="text-violet-100 text-sm">
                Upload, verify, and manage your financial documents
              </Text>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div class="bg-white/10 rounded-xl p-4">
              <Text class="text-violet-200 text-xs uppercase tracking-wide">Verified</Text>
              <div class="text-2xl font-bold text-emerald-300 mt-1">{{this.verifiedCount}}</div>
              <Text class="text-violet-200 text-xs">documents approved</Text>
            </div>
            <div class="bg-white/10 rounded-xl p-4">
              <Text class="text-violet-200 text-xs uppercase tracking-wide">Pending</Text>
              <div class="text-2xl font-bold text-amber-300 mt-1">{{this.pendingCount}}</div>
              <Text class="text-violet-200 text-xs">under review</Text>
            </div>
            <div class="bg-white/10 rounded-xl p-4">
              <Text class="text-violet-200 text-xs uppercase tracking-wide">Action Needed</Text>
              <div class="text-2xl font-bold text-red-300 mt-1">{{this.actionNeededCount}}</div>
              <Text class="text-violet-200 text-xs">require attention</Text>
            </div>
          </div>
        </div>
      </div>

      {{! Smart Actions }}
      <SectionHeader
        @icon={{PhLightbulb}}
        @title="Quick Actions"
        @subtitle="Manage your documents"
        @iconClass="text-amber-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        {{#each this.smartActions as |action|}}
          <SmartActionCard @item={{action}} />
        {{/each}}
      </div>

      {{! Document List }}
      <SectionHeader
        @icon={{PhFileText}}
        @title="Your Documents"
        @subtitle="All uploaded and verified documents"
        @iconClass="text-violet-500"
      />
      <div class="space-y-3">
        {{#each this.documents as |doc|}}
          <div
            class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between hover:shadow-md transition-shadow"
          >
            <div class="flex items-center gap-4">
              <div
                class="flex h-12 w-12 items-center justify-center rounded-lg
                  {{if
                    (eq doc.status 'verified')
                    'bg-green-100 dark:bg-green-900/30'
                    (if
                      (eq doc.status 'pending')
                      'bg-amber-100 dark:bg-amber-900/30'
                      'bg-red-100 dark:bg-red-900/30'
                    )
                  }}"
              >
                {{#if (eq doc.status "verified")}}
                  <PhCheckCircle class="h-6 w-6 text-green-600" />
                {{else if (eq doc.status "pending")}}
                  <PhClock class="h-6 w-6 text-amber-600" />
                {{else}}
                  <PhWarning class="h-6 w-6 text-red-600" />
                {{/if}}
              </div>
              <div>
                <div class="font-medium text-gray-900 dark:text-white">{{doc.name}}</div>
                <div class="flex items-center gap-2 mt-1">
                  <Text class="text-gray-500 text-xs">{{doc.type}}</Text>
                  <span class="text-gray-300">·</span>
                  <Text class="text-gray-500 text-xs">{{doc.uploadDate}}</Text>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-3">
              <Chip
                @intent={{if
                  (eq doc.status "verified")
                  "success"
                  (if (eq doc.status "pending") "warning" "danger")
                }}
              >
                {{doc.statusLabel}}
              </Chip>
              <button
                type="button"
                class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2"
              >
                <PhEye class="h-5 w-5" />
              </button>
            </div>
          </div>
        {{/each}}
      </div>

      {{! Upload Methods }}
      <SectionHeader
        @icon={{PhCloudArrowUp}}
        @title="Upload Methods"
        @subtitle="Choose how to share your documents"
        @iconClass="text-blue-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center"
        >
          <div
            class="flex h-14 w-14 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/30 mx-auto mb-4"
          >
            <PhCloudArrowUp class="h-7 w-7 text-violet-600" />
          </div>
          <Heading @level={{4}} class="font-semibold mb-2">File Upload</Heading>
          <Text class="text-gray-600 dark:text-gray-400 text-sm mb-4">
            Drag and drop or browse to upload PDF, JPG, or PNG documents.
          </Text>
          <Button @intent="primary" @appearance="outlined" class="w-full">
            Upload Files
          </Button>
        </div>

        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center"
        >
          <div
            class="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30 mx-auto mb-4"
          >
            <PhSparkle class="h-7 w-7 text-blue-600" />
          </div>
          <Heading @level={{4}} class="font-semibold mb-2">Upload Documents</Heading>
          <Text class="text-gray-600 dark:text-gray-400 text-sm mb-4">
            Upload your loan statements or NSLDS file for automatic data verification.
          </Text>
          <Button @intent="primary" @appearance="outlined" class="w-full">
            Upload Documents
          </Button>
        </div>

        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center"
        >
          <div
            class="flex h-14 w-14 items-center justify-center rounded-xl bg-teal-100 dark:bg-teal-900/30 mx-auto mb-4"
          >
            <PhRobot class="h-7 w-7 text-teal-600" />
          </div>
          <Heading @level={{4}} class="font-semibold mb-2">AI-Assisted</Heading>
          <Text class="text-gray-600 dark:text-gray-400 text-sm mb-4">
            Our AI reads and extracts data from your documents automatically.
          </Text>
          <Chip @intent="primary">Coming Soon</Chip>
        </div>
      </div>
    </VStack>
  </template>
}
